import axios, { AxiosResponse } from "axios";
import FileInterface, { FileResponseInterface } from "./FileInterface";
import base64toBlob from "helpers/file/base64ToBlob";

function mergeConfig(config: HashMap<any>): HashMap<any> {
    return config;
}

const API_URL = process.env.REACT_APP_SERVER_URL;

class fetch {
    get<T = any>(url: string, config: HashMap<any> = {}): CancellablePromise<AxiosResponse<T>> {
        const CancelToken = axios.CancelToken.source();
        let query: Partial<CancellablePromise<AxiosResponse<T>>> = axios.get(
            API_URL + url,
            mergeConfig(Object.assign({}, config, { cancelToken: CancelToken.token }))
        );
        query.cancel = CancelToken.cancel;
        return query as CancellablePromise<AxiosResponse<T>>;
    }

    delete<T = any>(url: string, config: HashMap<any> = {}): CancellablePromise<AxiosResponse<T>> {
        const CancelToken = axios.CancelToken.source();
        let query: Partial<CancellablePromise<AxiosResponse<T>>> = axios.delete(
            API_URL + url,
            mergeConfig(Object.assign({}, config, { cancelToken: CancelToken.token }))
        );
        query.cancel = CancelToken.cancel;
        return query as CancellablePromise<AxiosResponse<T>>;
    }

    post<T = any>(url: string, data?: any, config: HashMap<any> = {}): CancellablePromise<AxiosResponse<T>> {
        const CancelToken = axios.CancelToken.source();
        let query: Partial<CancellablePromise<AxiosResponse<T>>> = axios.post(
            API_URL + url,
            data,
            mergeConfig(Object.assign({}, config, { cancelToken: CancelToken.token }))
        );
        query.cancel = CancelToken.cancel;
        return query as CancellablePromise<AxiosResponse<T>>;
    }

    put<T = any>(url: string, data?: any, config: HashMap<any> = {}): CancellablePromise<AxiosResponse<T>> {
        const CancelToken = axios.CancelToken.source();
        let query: Partial<CancellablePromise<AxiosResponse<T>>> = axios.put(
            API_URL + url,
            data,
            mergeConfig(Object.assign({}, config, { cancelToken: CancelToken.token }))
        );
        query.cancel = CancelToken.cancel;
        return query as CancellablePromise<AxiosResponse<T>>;
    }

    patch<T = any>(url: string, data: any, config: HashMap<any> = {}): CancellablePromise<AxiosResponse<T>> {
        const CancelToken = axios.CancelToken.source();
        let query: Partial<CancellablePromise<AxiosResponse<T>>> = axios.patch(
            API_URL + url,
            data,
            mergeConfig(Object.assign({}, config, { cancelToken: CancelToken.token }))
        );
        query.cancel = CancelToken.cancel;
        return query as CancellablePromise<AxiosResponse<T>>;
    }
}

const query_debug = function (response: AxiosResponse<any>) {
    if (process.env.NODE_ENV === "development") {
        if (response.data && response.data.debug) {
            const debug = response.data.debug;
            const config = response.config;
            if (debug.warnings && debug.warnings.length) {
                console.groupCollapsed(
                    `%c Request warning : ${config.method} ${config.url}`,
                    "color: #856404; background-color: #fff3cd; border-color: #ffeeba;"
                );
                console.log(debug.warnings);
                console.groupEnd();
            }
            if (debug.custom_data) {
                console.groupCollapsed(
                    `%c Request custom data : ${config.method} ${config.url}`,
                    "color: #0c5460; background-color: #d1ecf1; border-color: #bee5eb;"
                );
                for (let index = 0; index < debug.custom_data.length; index++) {
                    const element = debug.custom_data[index];
                    console.log(element);
                }
                console.groupEnd();
            }
        }
    }
};

// Axios interceptor
axios.interceptors.response.use(
    function (response: AxiosResponse<any>) {
        query_debug(response);
        return response;
    },
    function (error: any) {
        if (error.response) {
            query_debug(error.response);
        }
        // Do something with response error
        return Promise.reject(error);
    }
);

const singleton = new fetch();
export default singleton;

function wrapPromiseParse<T, R>(
    fetcher: CancellablePromise<AxiosResponse<T>>,
    callback: (response: AxiosResponse<T>) => R
): CancellablePromise<R> {
    const promise = fetcher.then((response) => {
        return callback(response);
    }) as CancellablePromise<R>;

    promise.cancel = fetcher.cancel;

    return promise;
}

export function parseResponse<T>(fetcher: CancellablePromise<AxiosResponse<{ data: T }>>): CancellablePromise<T> {
    return wrapPromiseParse(fetcher, (response) => {
        return response.data.data;
    });
}

export function parseFileResponse(
    fetcher: CancellablePromise<AxiosResponse<{ data: FileResponseInterface }>>
): CancellablePromise<FileInterface> {
    return wrapPromiseParse(fetcher, (response) => {
        const data = { ...response.data.data };
        return Object.assign({}, data, { content: base64toBlob(data.content, data.type) });
    });
}

interface ListResponse<T> {
    data: Array<T>;
    pager: any;
    filters: any;
    warnings?: Array<string>;
}
export function parseListResponse<T>(fetcher: CancellablePromise<AxiosResponse<ListResponse<T>>>) {
    return wrapPromiseParse(fetcher, (response) => {
        const payload = response.data;
        return Object.assign([...payload.data], {
            active_filters: payload.filters,
            pager: payload.pager,
            warnings: payload.warnings || [],
        });
    });
}
